import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
  ReactNode,
  Dispatch,
  SetStateAction,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  callErrorToast,
  cleanObject,
  removeError,
} from "../../../utilities/utilities";
import { useAppContext } from "../../../AppProvider";
import { CompaniesService } from "../../../services/registry/companies.ts";
import {
  Company,
  CompanyType,
  defaultCreateCompany,
  validate,
} from "../../../Models/Company";
import { getCompanyPath } from "../utilities/index.ts";
import { createModalConfig, ModalConfig } from "../../../Models/ModalConfig.ts";
import { ErrorResponse } from "../../../Models/Error";

// Instantiate service classes for API calls
const companiesService = new CompaniesService();

/**
 * Props for the HandlerCompanyProvider component.
 */
interface HandlerCompanyProviderProps {
  children: ReactNode; // React children components that will be wrapped by the provider
  callback?: () => void; // Optional function executed after an update
  autosave: boolean; // Determines whether Company updates are saved automatically
  type: CompanyType; // Type of company: CUSTOMER, AFILIATE, ORGANIZATION
}

/**
 * Context value that will be shared with consumers.
 */
interface HandlerCompanyContextValue {
  data: Partial<Company>; // The Company data being managed
  error: ErrorResponse<Company> | null; // Error response if any request fails
  loader: boolean; // Indicates whether an operation is in progress
  getCompany: (id: string) => void; // Function to retrieve Company details
  action: string; // The current action (e.g., "create", "edit")
  edit: (Company: Company, property: keyof Company, save?: boolean) => void; // Function to update Company details
  type: CompanyType;
  modalConfig: ModalConfig;
  setModalConfig: Dispatch<SetStateAction<ModalConfig>>;
}

// Create the context with an initial value of undefined
const HandlerCompanyContext = createContext<
  HandlerCompanyContextValue | undefined
>(undefined);

/**
 * Provider component for managing Company-related data and operations.
 * Handles retrieval, creation, and modification of Companys.
 */
const HandlerCompanyProvider: React.FC<HandlerCompanyProviderProps> = ({
  children,
  autosave = false,
  callback,
  type,
}) => {
  const { id } = useParams(); // Get the Company ID from URL parameters
  const navigate = useNavigate();
  const { dictionary } = useAppContext();
  const action = window.location.pathname.split("/")[2]; // Extracts action from the URL

  // State management
  const [data, setData] = useState<Partial<Company>>({});
  const [loader, setLoader] = useState<boolean>(true);
  const [error, setError] = useState<ErrorResponse<Company> | null>(null);

  const prevError = useRef<ErrorResponse<Company>>(null);

  /**
   * Fetches Company details by ID.
   * @param id - The Company ID.
   */
  const getCompany = (id: string): void => {
    setLoader(true);
    companiesService
      .get(id)
      .then((res) => {
        setData(res.data);
        setLoader(false);
      })
      .catch((err) => {
        setLoader(false);
        setError(err);
      });
  };

  /**
   * Creates a new Company and navigates to the edit page.
   */
  const create = (): void => {
    const newCompany = defaultCreateCompany(type);
    companiesService
      .create(newCompany)
      .then((res) => {
        navigate(`/${getCompanyPath(type)}/edit/${res.data.id}`, {
          replace: true,
        });
      })
      .catch((err) => {
        callErrorToast(err);
      });

    return;
  };

  /**
   * Updates a Company's property and optionally saves it.
   * @param company - The updated Company object.
   * @param property - The property being modified.
   * @param save - Whether to persist the change immediately.
   */
  const edit = (
    company: Company,
    property: keyof Company,
    save = true
  ): void => {
    company = cleanObject(company);
    if (property) {
      removeError(property, error, prevError, setError);
    }
    if (save && autosave) {
      const validation = validate(company);
      if (!validation.isValid) {
        setError({ response: { data: validation.errors } });
        return;
      }

      companiesService
        .edit(company)
        .then(() => {
          setData(company);
          if (save && callback) {
            callback();
          }
        })
        .catch((err) => {
          setLoader(false);
          setError(err);
        });
      return;
    }
    setData(company);
  };

  useEffect(() => {
    if (error) {
      callErrorToast(error);
    }
  }, [error]);

  /**
   * Effect to initialize Company data based on the current action.
   */
  useEffect(() => {
    if (action === "create") {
      create();
      return;
    } else if (action === "clone") {
      return;
    } else if (id) {
      getCompany(id);
    }
  }, []);

  const [modalConfig, setModalConfig] = useState<ModalConfig>(
    createModalConfig()
  );

  return (
    <HandlerCompanyContext.Provider
      value={{
        data,
        loader,
        error,
        getCompany,
        edit,
        action,
        type,
        modalConfig,
        setModalConfig,
      }}
    >
      {children}
    </HandlerCompanyContext.Provider>
  );
};

/**
 * Custom hook to access the HandlerCompanyContext.
 * Ensures that the context is used only within a HandlerCompanyProvider.
 */
const useHandlerContext = (): HandlerCompanyContextValue => {
  const context = useContext(HandlerCompanyContext);
  if (!context) {
    throw new Error(
      "useHandlerCompanyContext must be used within a HandlerCompanyProvider"
    );
  }
  return context;
};

// Export the provider and custom hook for use in other components
export { HandlerCompanyProvider, useHandlerContext };
